import React from 'react';
import type { ComparisonGroup as ComparisonGroupType, ComparisonTicket } from '../../lib/types';
import { formatPrice } from '../../lib/format';
import {
  ArrowTopRightOnSquareIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  MinusIcon,
} from '@heroicons/react/24/outline';

function formatScrapedAt(scraped_at?: string | null): string | null {
  if (!scraped_at) return null;
  const d = new Date(scraped_at);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Effective "as of" date: prefer last_seen_at, fall back to scraped_at */
function effectiveDate(ticket: ComparisonTicket): string | null {
  return formatScrapedAt(ticket.last_seen_at ?? ticket.scraped_at);
}

const TICKETED_NAME = 'Ticketed';

interface ComparisonGroupProps {
  group: ComparisonGroupType;
}

function priceDiff(ticketedPrice: number, competitorPrice: number): { amount: number; pct: number; cheaper: boolean } {
  const amount = competitorPrice - ticketedPrice;
  const pct = (amount / ticketedPrice) * 100;
  return { amount, pct, cheaper: amount > 0 }; // cheaper = true means Ticketed is cheaper
}

export default function ComparisonGroup({ group }: ComparisonGroupProps) {
  const { group_label, tickets } = group;

  const ticketedTicket = tickets.find((t) => t.supplier.name === TICKETED_NAME) || null;
  const competitors = tickets.filter((t) => t.supplier.name !== TICKETED_NAME)
    .sort((a, b) => {
      if (a.price === null && b.price === null) return 0;
      if (a.price === null) return 1;
      if (b.price === null) return -1;
      return a.price - b.price;
    });

  // Only count available tickets for header badges
  const availableTickets = tickets.filter((t) => t.is_available !== false);
  const allPrices = availableTickets.filter((t) => t.price !== null).map((t) => t.price as number);
  const cheapestPrice = allPrices.length > 0 ? Math.min(...allPrices) : null;
  const ticketedIsCheapest = ticketedTicket?.is_available !== false
    && ticketedTicket?.price !== null
    && ticketedTicket?.price !== undefined
    && ticketedTicket.price === cheapestPrice
    && allPrices.length > 1;

  return (
    <div className="card overflow-hidden">
      {/* Group header */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-700/50 bg-slate-800/80">
        <h3 className="text-sm font-semibold text-slate-200 truncate flex-1">
          {group_label}
        </h3>
        {ticketedIsCheapest && (
          <span className="badge-cheapest text-[10px] uppercase tracking-wider shrink-0">
            Ticketed cheapest
          </span>
        )}
        {ticketedTicket && ticketedTicket.is_available !== false && !ticketedIsCheapest && ticketedTicket.price !== null && cheapestPrice !== null && ticketedTicket.price > cheapestPrice && (
          <span className="badge text-[10px] uppercase tracking-wider bg-rose-500/15 text-rose-400 border border-rose-500/25 shrink-0">
            Ticketed more expensive
          </span>
        )}
      </div>

      <div className="p-4 sm:p-5">
        {ticketedTicket ? (
          <TicketedAnchoredView
            ticketed={ticketedTicket}
            competitors={competitors}
            cheapestPrice={cheapestPrice}
          />
        ) : (
          <CompetitorsOnlyView
            competitors={competitors}
            cheapestPrice={cheapestPrice}
          />
        )}
      </div>
    </div>
  );
}

/* ─── Ticketed-anchored layout ────────────────────────────────────────────── */

function TicketedAnchoredView({
  ticketed,
  competitors,
  cheapestPrice,
}: {
  ticketed: ComparisonTicket;
  competitors: ComparisonTicket[];
  cheapestPrice: number | null;
}) {
  const ticketedPrice = ticketed.price;
  const ticketedAvailable = ticketed.is_available !== false;

  return (
    <div className="space-y-4">
      {/* Ticketed price — prominent */}
      <div className={`flex items-center gap-4 p-4 rounded-lg border ${
        !ticketedAvailable
          ? 'bg-slate-800/40 border-slate-700/40'
          : ticketedPrice !== null && ticketedPrice === cheapestPrice && competitors.length > 0
            ? 'bg-emerald-500/8 border-emerald-500/25'
            : 'bg-sky-500/5 border-sky-500/20'
      }`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-bold uppercase tracking-wider ${ticketedAvailable ? 'text-sky-400' : 'text-slate-500'}`}>Ticketed</span>
            {ticketedAvailable && ticketedPrice !== null && ticketedPrice === cheapestPrice && competitors.length > 0 && (
              <span className="text-[10px] text-emerald-400 font-medium">Best price</span>
            )}
            {!ticketedAvailable && (
              <span className="text-[10px] font-medium text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded px-1.5 py-0.5">
                No longer available
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-500 truncate">{ticketed.title}</p>
        </div>
        <div className="text-right shrink-0">
          {ticketedAvailable ? (
            <>
              <p className={`text-2xl font-bold ${
                ticketedPrice !== null && ticketedPrice === cheapestPrice && competitors.length > 0
                  ? 'text-emerald-400'
                  : 'text-sky-400'
              }`}>
                {formatPrice(ticketedPrice)}
              </p>
              {effectiveDate(ticketed) && (
                <p className="text-[10px] text-slate-500 mt-0.5">as of {effectiveDate(ticketed)}</p>
              )}
            </>
          ) : (
            <>
              <p className="text-2xl font-bold text-slate-600">
                {formatPrice(ticketedPrice)}
              </p>
              {formatScrapedAt(ticketed.unavailable_since) && (
                <p className="text-[10px] text-rose-400/70 mt-0.5">Unavailable since {formatScrapedAt(ticketed.unavailable_since)}</p>
              )}
              {effectiveDate(ticketed) && (
                <p className="text-[10px] text-slate-600 mt-0.5">as of {effectiveDate(ticketed)}</p>
              )}
            </>
          )}
        </div>
        {ticketedAvailable && (
          <a
            href={ticketed.url}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-slate-500 hover:text-sky-400 transition-colors"
          >
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
          </a>
        )}
      </div>

      {/* Competitor comparison rows */}
      {competitors.length > 0 && (
        <div className="space-y-2">
          <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500 px-1">
            vs Competitors
          </p>
          {competitors.map((comp) => {
            const compAvailable = comp.is_available !== false;
            const diff = ticketedPrice !== null && comp.price !== null
              ? priceDiff(ticketedPrice, comp.price)
              : null;

            return (
              <div
                key={comp.ticket_id}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800/60 border border-slate-700/40 ${
                  !compAvailable ? 'opacity-60' : ''
                }`}
              >
                {/* Supplier name & title */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-300">{comp.supplier.name}</p>
                    {!compAvailable && (
                      <span className="text-[10px] font-medium text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded px-1.5 py-0.5">
                        No longer available
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 truncate">{comp.title}</p>
                </div>

                {/* Competitor price */}
                <div className="text-right shrink-0">
                  <p className={`text-sm font-semibold ${compAvailable ? 'text-slate-200' : 'text-slate-500'}`}>
                    {formatPrice(comp.price)}
                  </p>
                  {effectiveDate(comp) && (
                    <p className="text-[10px] text-slate-500 mt-0.5">as of {effectiveDate(comp)}</p>
                  )}
                </div>

                {/* Diff indicator */}
                <div className="w-24 shrink-0 text-right">
                  {!compAvailable ? (
                    formatScrapedAt(comp.unavailable_since) ? (
                      <p className="text-[10px] text-rose-400/70">Unavailable since {formatScrapedAt(comp.unavailable_since)}</p>
                    ) : (
                      <span className="text-xs text-slate-500">--</span>
                    )
                  ) : diff ? (
                    diff.amount === 0 ? (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                        <MinusIcon className="w-3 h-3" />
                        Same
                      </span>
                    ) : diff.cheaper ? (
                      <div>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-400">
                          <ArrowTrendingDownIcon className="w-3.5 h-3.5" />
                          {formatPrice(Math.abs(diff.amount))}
                        </span>
                        <p className="text-[10px] text-emerald-400/60">
                          {Math.abs(diff.pct).toFixed(0)}% cheaper
                        </p>
                      </div>
                    ) : (
                      <div>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-400">
                          <ArrowTrendingUpIcon className="w-3.5 h-3.5" />
                          {formatPrice(Math.abs(diff.amount))}
                        </span>
                        <p className="text-[10px] text-rose-400/60">
                          {Math.abs(diff.pct).toFixed(0)}% more
                        </p>
                      </div>
                    )
                  ) : (
                    <span className="text-xs text-slate-500">--</span>
                  )}
                </div>

                {/* View link */}
                {compAvailable && (
                  <a
                    href={comp.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-slate-500 hover:text-emerald-400 transition-colors"
                  >
                    <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Competitors-only layout (Ticketed not in this group) ────────────────── */

function CompetitorsOnlyView({
  competitors,
  cheapestPrice,
}: {
  competitors: ComparisonTicket[];
  cheapestPrice: number | null;
}) {
  return (
    <div>
      <p className="text-[11px] font-medium text-amber-400/80 bg-amber-500/8 border border-amber-500/15 rounded-lg px-3 py-2 mb-4">
        Ticketed doesn't have a matching ticket in this category
      </p>
      <div className="space-y-2">
        {competitors.map((comp) => {
          const compAvailable = comp.is_available !== false;

          return (
            <div
              key={comp.ticket_id}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800/60 border border-slate-700/40 ${
                !compAvailable ? 'opacity-60' : ''
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-slate-300">{comp.supplier.name}</p>
                  {!compAvailable && (
                    <span className="text-[10px] font-medium text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded px-1.5 py-0.5">
                      No longer available
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 truncate">{comp.title}</p>
              </div>
              <div className="text-right shrink-0">
                {compAvailable ? (
                  <>
                    <p className={`text-sm font-semibold ${
                      comp.price === cheapestPrice && competitors.length > 1
                        ? 'text-emerald-400'
                        : 'text-slate-200'
                    }`}>
                      {formatPrice(comp.price)}
                    </p>
                    {effectiveDate(comp) && (
                      <p className="text-[10px] text-slate-500 mt-0.5">as of {effectiveDate(comp)}</p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-slate-500">
                      {formatPrice(comp.price)}
                    </p>
                    {formatScrapedAt(comp.unavailable_since) && (
                      <p className="text-[10px] text-rose-400/70 mt-0.5">Unavailable since {formatScrapedAt(comp.unavailable_since)}</p>
                    )}
                    {effectiveDate(comp) && (
                      <p className="text-[10px] text-slate-600 mt-0.5">as of {effectiveDate(comp)}</p>
                    )}
                  </>
                )}
              </div>
              {compAvailable && (
                <a
                  href={comp.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 text-slate-500 hover:text-emerald-400 transition-colors"
                >
                  <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
